import React, { useEffect } from 'react';
import styled from 'styled-components';
import _ from 'lodash';
import { useHistory } from 'react-router-dom';
import { Code, ChatBubble, LightbulbOutline, ShowChart, Sync } from '@sparkpost/matchbox-icons';
import SendingMailWebp from '@sparkpost/matchbox-media/images/Sending-Mail.webp';
import SendingMail from '@sparkpost/matchbox-media/images/Sending-Mail@medium.jpg';
import ConfigurationWebp from '@sparkpost/matchbox-media/images/Configuration.webp';
import Configuration from '@sparkpost/matchbox-media/images/Configuration@medium.jpg';
import { Loading, Abbreviation } from 'src/components';
import {
  Box,
  Button,
  Columns,
  Column,
  Layout,
  Panel,
  Picture,
  ScreenReaderOnly,
  Stack,
  Text
} from 'src/components/matchbox';
import { Bold, Heading, TranslatableText } from 'src/components/text';
import { ExternalLink, PageLink, SupportTicketLink } from 'src/components/links';
import {
  AnalyticsReportCharts,
  CompareByAggregatedMetrics,
  AggregatedMetrics
} from 'src/components/analyticsReport';
import { getFormattedDateRangeForAggregateData } from '@sparkpost/report-builder/helpers/date';
import { isSubaccountDeveloperUser } from 'src/helpers/conditions/user';
import { usePinnedReport, usePrevious } from 'src/hooks';
import useDashboardContext from './hooks/useDashboardContext';
import Dashboard from './components/Dashboard';
import Sidebar from './components/Sidebar';
import { LINKS, ONBOARDING_STEP } from 'src/constants';
import { useModal } from 'src/hooks';
import ChangeReportModal from './components/ChangeReportModal';
import { AsyncActionModal } from 'src/components';
import { ActiveFilters } from 'src/components/analyticsReport';
import { RUDDERSTACK_EVENTS } from 'src/helpers/rudderstack';
import track from 'src/helpers/track';
import { AccessControl } from 'src/components/auth';
import { hasGrants } from 'src/helpers/conditions';

const OnboardingImg = styled(Picture.Image)`
  vertical-align: bottom;
`;

export default function DashboardPage() {
  const {
    canViewUsage,
    canManageSendingDomains,
    canManageApiKeys,
    canManageReports,
    canManageAlerts,
    getAccount,
    getReports,
    listAlerts,
    getUsage,
    verifySendingLink,
    onboarding,
    isAnAdmin,
    isDev,
    currentUser,
    pending,
    listSendingDomains,
    listApiKeys,
    reports,
    lastUsageDate,
    usageLoading
  } = useDashboardContext();
  const allReports = reports.map((report) => ({ ...report, key: report.id }));
  const hasSetupDocumentationPanel = isAnAdmin || isDev;
  const history = useHistory();
  const usageLoaded = usePrevious(usageLoading) && !usageLoading;

  useEffect(() => {
    if (onboarding !== ONBOARDING_STEP.PINNED_REPORT) {
      track(RUDDERSTACK_EVENTS.DASHBOARD_ONBOARDING, {
        onboarding
      });
    }
  }, [onboarding]);

  useEffect(() => {
    getAccount();
    if (canManageAlerts) listAlerts();
    if (canViewUsage) getUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (lastUsageDate && usageLoaded && canManageReports) getReports();
  }, [getReports, lastUsageDate, usageLoaded, canManageReports]);

  useEffect(() => {
    if (canManageSendingDomains && !lastUsageDate && usageLoaded) listSendingDomains();

    if (canManageApiKeys && !lastUsageDate && usageLoaded) listApiKeys();
  }, [
    canManageApiKeys,
    canManageSendingDomains,
    lastUsageDate,
    listApiKeys,
    listSendingDomains,
    usageLoaded
  ]);

  const { closeModal, openModal, isModalOpen, meta: { name } = {} } = useModal();
  const isSubaccountDev = isSubaccountDeveloperUser({ currentUser });

  const { pinnedReport } = usePinnedReport(onboarding, reports);

  const dateValue = getFormattedDateRangeForAggregateData(
    pinnedReport?.options?.from,
    pinnedReport?.options?.to,
    pinnedReport?.options?.timezone
  );
  if (pending) return <Loading />;

  return (
    <Dashboard>
      <ScreenReaderOnly>
        <Heading as="h1">Dashboard</Heading>
      </ScreenReaderOnly>

      <ChangeReportModal
        open={isModalOpen && name === 'Change Report'}
        onClose={closeModal}
        reports={allReports}
      />

      <AsyncActionModal
        open={isModalOpen && name === 'Filters'}
        actionVerb="View Report"
        onAction={() => history.push(pinnedReport.linkToAnalyticsReport)}
        onCancel={closeModal}
        title={`${pinnedReport.name} Filters `}
        maxWidth="1300"
      >
        <ActiveFilters filters={pinnedReport.options.filters} />
      </AsyncActionModal>

      <Stack>
        {currentUser?.first_name && (
          <Dashboard.Heading>
            <TranslatableText>Welcome, </TranslatableText>
            {currentUser.first_name}!
          </Dashboard.Heading>
        )}

        <Layout>
          <Layout.Section>
            <Stack>
              {onboarding === ONBOARDING_STEP.PINNED_REPORT && (
                <AccessControl condition={hasGrants('metrics/view', 'reports/manage')}>
                  <Dashboard.Panel>
                    <Panel.Header>
                      <Panel.Headline>{pinnedReport.name}</Panel.Headline>

                      <Panel.Action
                        onClick={() => history.push(pinnedReport.linkToAnalyticsReport)}
                      >
                        <TranslatableText>View Report</TranslatableText>{' '}
                        <Button.Icon as={ShowChart} />
                      </Panel.Action>

                      <Panel.Action onClick={() => openModal({ name: 'Change Report' })}>
                        <TranslatableText>Change Report</TranslatableText> <Button.Icon as={Sync} />
                      </Panel.Action>
                    </Panel.Header>

                    {!pinnedReport.loading && (
                      <AnalyticsReportCharts
                        reportOptions={pinnedReport.options}
                        p="0"
                        small={true}
                      />
                    )}

                    {pinnedReport.options.comparisons.length > 0 ? (
                      <CompareByAggregatedMetrics
                        date={dateValue}
                        reportOptions={pinnedReport.options}
                        handleViewFiltersClick={() => openModal({ name: 'Filters' })}
                        showFiltersButton={pinnedReport?.options?.filters.length > 0}
                      />
                    ) : (
                      <AggregatedMetrics
                        date={dateValue}
                        reportOptions={pinnedReport.options}
                        handleViewFiltersClick={() => openModal({ name: 'Filters' })}
                        showFiltersButton={pinnedReport?.options?.filters.length > 0}
                      />
                    )}
                  </Dashboard.Panel>
                </AccessControl>
              )}
              {onboarding === ONBOARDING_STEP.ANALYTICS_REPORT_PROMO && (
                <Dashboard.Panel>
                  <Columns>
                    <Column>
                      <Panel.Header>
                        <Panel.Headline>Analytics Report</Panel.Headline>
                      </Panel.Header>

                      <Panel.Section>
                        <Stack>
                          <Text>
                            Build custom analytics, track engagement, diagnose errors, and more.
                          </Text>
                          <div>
                            <PageLink variant="primary" to="/signals/analytics" as={Button}>
                              Go To Analytics Report
                            </PageLink>
                          </div>
                        </Stack>
                      </Panel.Section>
                    </Column>
                    <Box as={Column} display={['none', 'none', 'block']} width={[0, 0, 0.5]}>
                      <Dashboard.OnboardingPicture>
                        <source srcSet={ConfigurationWebp} type="image/webp" />
                        <OnboardingImg alt="" src={Configuration} seeThrough />
                      </Dashboard.OnboardingPicture>
                    </Box>
                  </Columns>
                </Dashboard.Panel>
              )}

              {onboarding === ONBOARDING_STEP.ADD_SENDING_DOMAIN && (
                <Dashboard.Panel>
                  <Columns>
                    <Column>
                      <Panel.Header>
                        <Panel.Headline>Get Started!</Panel.Headline>
                      </Panel.Header>

                      <Panel.Section>
                        <Stack>
                          <Text>
                            <TranslatableText>At least one&nbsp;</TranslatableText>
                            <Bold>verified sending domain&nbsp;</Bold>
                            <TranslatableText>
                              is required in order to start or enable analytics.
                            </TranslatableText>
                          </Text>
                          <div>
                            <PageLink variant="primary" to="/domains/list/sending" as={Button}>
                              Add Sending Domain
                            </PageLink>
                          </div>
                        </Stack>
                      </Panel.Section>
                    </Column>
                    <Box as={Column} display={['none', 'none', 'block']} width={[0, 0, 0.5]}>
                      <Dashboard.OnboardingPicture>
                        <source srcSet={SendingMailWebp} type="image/webp" />
                        <OnboardingImg alt="" src={SendingMail} seeThrough />
                      </Dashboard.OnboardingPicture>
                    </Box>
                  </Columns>
                </Dashboard.Panel>
              )}

              {onboarding === ONBOARDING_STEP.VERIFY_SENDING_DOMAIN && (
                <Dashboard.Panel>
                  <Columns>
                    <Column>
                      <Panel.Header>
                        <Panel.Headline>Get Started!</Panel.Headline>
                      </Panel.Header>

                      <Panel.Section>
                        <Stack>
                          <Text>
                            <TranslatableText>
                              Once a sending domain has been added, it needs to be
                            </TranslatableText>
                            <Bold>&nbsp;verified.&nbsp;</Bold>
                            <TranslatableText>
                              Follow the instructions on the domain details page to configure your
                            </TranslatableText>
                            <TranslatableText>&nbsp;DNS settings.</TranslatableText>
                          </Text>
                          <div>
                            <PageLink variant="primary" to={verifySendingLink} as={Button}>
                              Verify Sending Domain
                            </PageLink>
                          </div>
                        </Stack>
                      </Panel.Section>
                    </Column>
                    <Box as={Column} display={['none', 'none', 'block']} width={[0, 0, 0.5]}>
                      <Dashboard.OnboardingPicture>
                        <source srcSet={SendingMailWebp} type="image/webp" />
                        <OnboardingImg alt="" src={SendingMail} seeThrough />
                      </Dashboard.OnboardingPicture>
                    </Box>
                  </Columns>
                </Dashboard.Panel>
              )}

              {onboarding === ONBOARDING_STEP.CREATE_API_KEY && (
                <Dashboard.Panel>
                  <Columns>
                    <Column>
                      <Panel.Header>
                        <Panel.Headline>Start Sending!</Panel.Headline>
                      </Panel.Header>

                      <Panel.Section>
                        <Stack>
                          <Text>
                            <TranslatableText>Create an&nbsp;</TranslatableText>
                            <Abbreviation title="Application Programming Interface">
                              API&nbsp;
                            </Abbreviation>
                            <TranslatableText>
                              key in order to start sending via API
                            </TranslatableText>
                            <TranslatableText>&nbsp;or</TranslatableText>
                            <Abbreviation title="Simple Mail Transfer Protocol">
                              &nbsp;SMTP.
                            </Abbreviation>
                          </Text>
                          <div>
                            <PageLink variant="primary" to="/account/api-keys/create" as={Button}>
                              Create API Key
                            </PageLink>
                          </div>
                        </Stack>
                      </Panel.Section>
                    </Column>
                    <Box as={Column} display={['none', 'none', 'block']} width={[0, 0, 0.5]}>
                      <Dashboard.OnboardingPicture>
                        <source srcSet={ConfigurationWebp} type="image/webp" />
                        <OnboardingImg alt="" src={Configuration} seeThrough />
                      </Dashboard.OnboardingPicture>
                    </Box>
                  </Columns>
                </Dashboard.Panel>
              )}

              {onboarding === ONBOARDING_STEP.START_SENDING && (
                <Dashboard.Panel>
                  <Columns>
                    <Column>
                      <Panel.Header>
                        <Panel.Headline>Start Sending!</Panel.Headline>
                      </Panel.Header>

                      <Panel.Section>
                        <Stack>
                          <Text>
                            <TranslatableText>
                              Follow the Getting Started documentation to set up sending via&nbsp;
                            </TranslatableText>
                            <Abbreviation title="Application Programming Interface">
                              API&nbsp;
                            </Abbreviation>
                            <TranslatableText>or</TranslatableText>
                            <Abbreviation title="Simple Mail Transfer Protocol">
                              &nbsp;SMTP.
                            </Abbreviation>
                          </Text>
                          <div>
                            <ExternalLink
                              variant="primary"
                              size="default"
                              showIcon={false}
                              to={LINKS.ONBOARDING_SENDING_EMAIL}
                              as={Button}
                            >
                              Getting Started Documentation
                            </ExternalLink>
                          </div>
                        </Stack>
                      </Panel.Section>
                    </Column>
                    <Box as={Column} display={['none', 'none', 'block']} width={[0, 0, 0.5]}>
                      <Dashboard.OnboardingPicture>
                        <source srcSet={ConfigurationWebp} type="image/webp" />
                        <OnboardingImg alt="" src={Configuration} seeThrough />
                      </Dashboard.OnboardingPicture>
                    </Box>
                  </Columns>
                </Dashboard.Panel>
              )}

              <div data-id="dashboard-helpful-shortcuts">
                <Dashboard.Panel>
                  <Panel.Header>
                    <Panel.Headline>
                      <Panel.HeadlineIcon as={LightbulbOutline} />
                      <TranslatableText>Helpful Shortcuts</TranslatableText>
                    </Panel.Headline>
                  </Panel.Header>

                  <Panel.Section>
                    <Columns collapseBelow="md">
                      {isAnAdmin && (
                        <Dashboard.Tip>
                          <PageLink to="/account/users/create">Invite a Team Member</PageLink>
                          <Text>
                            Need help integrating? Want to share an Analytics Report? Invite your
                            team!
                          </Text>
                        </Dashboard.Tip>
                      )}
                      {!isAnAdmin && (
                        <Dashboard.Tip>
                          <PageLink to="/templates">Templates</PageLink>
                          <Text>
                            Programmatically tailor each message with SparkPost’s flexible
                            templates.
                          </Text>
                        </Dashboard.Tip>
                      )}
                      <Dashboard.Tip>
                        <PageLink to="/reports/message-events">Events</PageLink>
                        <Text>
                          Robust searching capabilities with ready access to the raw event data from
                          your emails.
                        </Text>
                      </Dashboard.Tip>
                      <Dashboard.Tip>
                        <ExternalLink to={LINKS.INBOX_TRACKER}>Inbox Tracker</ExternalLink>
                        <Text>
                          Examine every element of deliverability with precision using Inbox
                          Tracker.
                        </Text>
                      </Dashboard.Tip>
                    </Columns>
                  </Panel.Section>
                </Dashboard.Panel>
              </div>
              <Columns collapseBelow="md" space="500">
                {hasSetupDocumentationPanel && (
                  <Column>
                    <Dashboard.Panel>
                      <Panel.Header>
                        <Panel.Headline>
                          <Panel.HeadlineIcon as={Code} />

                          <TranslatableText>Setup Documentation</TranslatableText>
                        </Panel.Headline>
                      </Panel.Header>

                      <Panel.Section>
                        <ExternalLink to={LINKS.INTEGRATION_DOCS}>
                          Integration Documentation
                        </ExternalLink>
                      </Panel.Section>
                    </Dashboard.Panel>
                  </Column>
                )}
                {!isSubaccountDev && (
                  <Column>
                    <Dashboard.Panel>
                      <Panel.Header>
                        <Panel.Headline>
                          <Panel.HeadlineIcon as={ChatBubble} />

                          <TranslatableText>Need Help?</TranslatableText>
                        </Panel.Headline>
                      </Panel.Header>

                      <Panel.Section>
                        <SupportTicketLink>Contact our Support Team</SupportTicketLink>
                      </Panel.Section>
                    </Dashboard.Panel>
                  </Column>
                )}

                {/* Used to shift the "Need Help?" Panel to align to the left */}
                {!hasSetupDocumentationPanel && <Column />}
              </Columns>
            </Stack>
          </Layout.Section>

          <Layout.Section annotated>
            <Sidebar>
              <Sidebar.AccountDetails />
              <Sidebar.BillingUsage />
              <Sidebar.RecentAlerts />
            </Sidebar>
          </Layout.Section>
        </Layout>
      </Stack>
    </Dashboard>
  );
}
